<script lang="ts" setup>
import { observeSizeChange } from '@@/bits/resize_observer'
import { defineAsyncComponent } from '@@/bits/vue'
import OzPlainButton, { OzPlainButtonColorScheme } from '@@/library/v4/components/OzPlainButton.vue'
import tinykeys from 'tinykeys'
import type { ComponentPublicInstance } from 'vue'
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch, withDefaults } from 'vue'

const OzIcon = defineAsyncComponent(() => import('@@/library/v4/components/OzIcon.vue'))

const props = withDefaults(
  defineProps<{
    /**
     * Tabs to display.
     */
    tabs: OzTabsButtonProps[]
    /**
     * Label corresponding to the tab list.
     */
    ariaLabel: string
    accessibilityRole?: OzTabsAccessibilityRole
    placementMode?: OzTabsPlacementMode
    /**
     * Key used to detect the current tab.
     */
    currentTabKey?: string
    /**
     * Tabs size preset.
     * Small: Font size: body-small, Indicator height: 1.5px
     * ExtraSmall: Font size: body-extra-small, Indicator height: 1px
     */
    sizePreset?: OzTabsSizePreset
    darkMode?: boolean | 'auto'
    /**
     * Classes applied to the tabs container.
     */
    tabsContainerClasses?: string
    /**
     * Override indicator width.
     */
    indicatorWidth?: number
    /**
     * Override indicator height.
     */
    indicatorHeight?: number
  }>(),
  {
    accessibilityRole: OzTabsAccessibilityRole.Tabs,
    placementMode: OzTabsPlacementMode.Hug,
    sizePreset: OzTabsSizePreset.Small,
    darkMode: 'auto',
    tabsContainerClasses: undefined,
    indicatorWidth: 0,
    indicatorHeight: 0,
    currentTabKey: undefined,
  },
)

const currentTabId = computed(() => props.currentTabKey || props.tabs[0].key)

interface Tab {
  key: string
  el: HTMLElement
}

const emit = defineEmits<{
  (
    e: 'activate-tab',
    event: Event,
    tab: {
      key: string
    },
  ): void
}>()

const resizeObserver = ref<ResizeObserver | null>(null)
const rootEl = ref<HTMLDivElement | null>(null)
const indicator = ref<HTMLDivElement | null>(null)
const list = ref<HTMLUListElement | null>(null)
const tabsRef = ref<Array<Tab | null>>(Array(props.tabs.length).fill(null))

const setTabRef = (el: Element | ComponentPublicInstance | null, index: number, tab: OzTabsButtonProps) => {
  if (el === null) return
  tabsRef.value[index] = { key: tab.key, el: el as HTMLElement }
}

const highlightedElement = computed<HTMLDivElement | null>(() => {
  const tabEl = tabsRef.value.find((tab) => tab?.key === currentTabId.value)?.el as HTMLDivElement
  return tabEl ?? null
})

const wrapperElement = computed(() => {
  return props.accessibilityRole === OzTabsAccessibilityRole.Tabs ? 'div' : 'nav'
})

const wrapperAttributes = computed(() => {
  return props.accessibilityRole === OzTabsAccessibilityRole.Tabs ? {} : { 'aria-label': props.ariaLabel }
})

const listAttributes = computed(() => {
  return isUsingTabsAccessibilityRole.value ? { 'aria-label': props.ariaLabel } : {}
})

const listRole = computed(() => {
  return isUsingTabsAccessibilityRole.value ? 'tablist' : 'list'
})

const listItemRole = computed(() => {
  return isUsingTabsAccessibilityRole.value ? 'presentation' : 'listitem'
})

const isUsingTabsAccessibilityRole = computed(() => {
  return props.accessibilityRole === OzTabsAccessibilityRole.Tabs
})

function computeTabAttributes(tab: OzTabsButtonProps): Record<string, any> {
  return isUsingTabsAccessibilityRole.value
    ? {
        id: tab.key,
        role: 'tab',
        'aria-controls': tab.ariaControls,
        'aria-label': tab.iconName ? tab.text : undefined,
        'aria-selected': currentTabId.value === tab.key ? 'true' : 'false',
        tabindex: currentTabId.value === tab.key ? 0 : -1,
      }
    : {
        role: tab.href ? 'link' : 'button',
      }
}

const repositionIndicator = async () => {
  await nextTick()
  if (!highlightedElement.value) return

  const indicatorElement = indicator.value
  if (indicatorElement === null) return

  // If indicator height is provided, override the height from the size preset
  if (props.indicatorHeight) {
    indicatorElement.style.height = props.indicatorHeight + 'px'
  }

  if (props.indicatorWidth) {
    indicatorElement.style.width = props.indicatorWidth + 'px'
    indicatorElement.style.left =
      highlightedElement.value.offsetLeft + (highlightedElement.value.clientWidth - props.indicatorWidth) / 2 + 'px'
  } else {
    indicatorElement.style.width = highlightedElement.value.offsetWidth + 'px'
    indicatorElement.style.left = highlightedElement.value.offsetLeft + 'px'
  }
}

/**
 * Focus the first tab.
 */
function focusFirstTab(): void {
  if (tabsRef.value[0] === null) return
  const el = tabsRef.value[0].el?.firstChild as HTMLElement
  el.focus()
}

/**
 * Focus the last tab.
 */
function focusLastTab(): void {
  if (tabsRef.value.length === 0) return
  const lastTabEl = tabsRef.value[tabsRef.value.length - 1]
  if (lastTabEl !== null) {
    const el = lastTabEl.el.firstChild as HTMLElement
    el.focus()
  }
}

/**
 * When pressing "Home", focus the first tab.
 */
function handleHomeKey(event: KeyboardEvent): void {
  event.preventDefault()
  focusFirstTab()
}

/**
 * When pressing "End", focus the last tab.
 */
function handleEndKey(event: KeyboardEvent): void {
  event.preventDefault()
  focusLastTab()
}

/**
 * When pressing the left arrow, focus the previous tab.
 * If on first tab, focus the last tab.
 */
function handleArrowLeft(event: KeyboardEvent): void {
  const target = event.target as HTMLElement
  const wrapper = target.parentElement
  if (wrapper?.previousElementSibling) {
    const previousSibling = wrapper.previousElementSibling.firstChild as HTMLElement
    previousSibling?.focus()
  } else {
    focusLastTab()
  }
}

/**
 * When pressing the right arrow, focus the next tab.
 * If on last tab, focus the first tab.
 */
function handleArrowRight(event: KeyboardEvent): void {
  const target = event.target as HTMLElement
  const wrapper = target.parentElement
  if (wrapper?.nextElementSibling) {
    const nextSibling = wrapper.nextElementSibling.firstChild as HTMLElement
    nextSibling?.focus()
  } else {
    focusFirstTab()
  }
}

watch(
  [
    () => currentTabId.value,
    () => props.placementMode,
    () => props.sizePreset,
    () => props.tabsContainerClasses,
    () => props.indicatorWidth,
  ],
  repositionIndicator,
  { immediate: true },
)

let removeKeyboardShortcutsListener = () => {}

onMounted(() => {
  if (rootEl.value) {
    resizeObserver.value = observeSizeChange(rootEl.value, repositionIndicator)
  }
  if (list.value) {
    removeKeyboardShortcutsListener = tinykeys(list.value, {
      Home: handleHomeKey,
      End: handleEndKey,
      ArrowLeft: handleArrowLeft,
      ArrowRight: handleArrowRight,
    })
  }
})

onBeforeUnmount(() => {
  if (rootEl.value) {
    resizeObserver.value?.disconnect()
    resizeObserver.value = null
  }
  removeKeyboardShortcutsListener()
})
</script>

<script lang="ts">
interface OzTabsButtonProps {
  /**
   * A string to identify the tab.
   */
  // eslint-disable-next-line vue/no-reserved-props
  key: string
  /**
   * The text to display on the tab.
   * If `iconName` is provided, this will be used as the `aria-label` instead.
   */
  text: string
  /**
   * The name of the icon to display on the tab.
   * We don't display `text` if this is provided.
   */
  iconName?: string
  /**
   * The name of the icon to display on the tab when it's active.
   */
  activeIconName?: string
  /**
   * The size of the icon to display on the tab.
   */
  iconSize?: 16 | 24
  /**
   * `aria-controls` attribute.
   */
  ariaControls?: string
  /**
   * `href` attribute.
   * This also makes the tab a link.
   */
  href?: string
  /**
   * should the tab be disabled or not
   */
  disabled?: boolean
  /**
   * Id for querying tab in tests
   */
  testId?: string
}

/**
 * Accommodates the elements and attributes according to the use.
 * If 'Tabs', the component will be structured according to the ARIA tab list specification.
 * If 'Navigation', the component will be structured according to the ARIA navigation specification.
 */
enum OzTabsAccessibilityRole {
  Tabs = 'Tabs',
  Navigation = 'Navigation',
}

/**
 * Tabs placement mode.
 * Hug: Gap between tabs is dynamic, and each tab occupies as little space as possible.
 * Stretch: No gap between tabs, and each tab has the same width.
 */
enum OzTabsPlacementMode {
  Hug = 'Hug',
  Stretch = 'Stretch',
}

enum OzTabsSizePreset {
  ExtraSmall = 'ExtraSmall',
  Small = 'Small',
}
export { OzTabsAccessibilityRole, OzTabsPlacementMode, OzTabsSizePreset }
export type { OzTabsButtonProps }
export default {}
</script>

<template>
  <component :is="wrapperElement" v-bind="wrapperAttributes" ref="rootEl" class="relative">
    <ul
      ref="list"
      :class="[
        'list-none',
        // Placement
        'flex',
        'justify-between',
        // Spacing
        'm-0',
        'p-0',
        // Custom classes
        tabsContainerClasses,
      ]"
      v-bind="listAttributes"
      :role="listRole"
    >
      <li
        v-for="(tab, index) in tabs"
        :key="tab.key"
        :ref="(el) => setTabRef(el, index, tab)"
        :data-testid="tab.testId"
        :data-pw="tab.testId"
        :class="[
          // Placement
          'flex',
          'items-center',
          // Size
          placementMode === OzTabsPlacementMode.Stretch && 'flex-1',
        ]"
        :role="listItemRole"
      >
        <OzPlainButton
          :class="[
            'flex',
            'justify-center',

            'outline-none',
            'rounded-xl',
            // Size
            'w-full',
            // Spacing
            'px-2 pt-2 pb-2.25',
            // Font
            'font-semibold',
            'text-center',
            sizePreset === OzTabsSizePreset.Small && 'text-body-small',
            sizePreset === OzTabsSizePreset.ExtraSmall && 'text-body-extra-small',
            // Base
            currentTabId === tab.key
              ? {
                  'text-grape-500': darkMode === false,
                  'text-canary-500': darkMode === true,
                  'text-grape-500 dark:text-canary-500': darkMode === 'auto',
                }
              : {
                  'text-dark-text-200': darkMode === false,
                  'text-light-text-200': darkMode === true,
                  'text-dark-text-200 dark:text-light-text-200': darkMode === 'auto',
                },
            // Hovered
            !tab.disabled && {
              'hover-hover:hover:text-grape-500': darkMode === false,
              'hover-hover:hover:text-canary-500': darkMode === true,
              'hover-hover:hover:text-grape-500 hover-hover:dark:hover:text-canary-500': darkMode === 'auto',
            },
            // Focused
            'ring-inset',
            'focus-visible:ring-2',
            {
              'focus-visible:ring-grape-500': darkMode === false,
              'focus-visible:ring-canary-500': darkMode === true,
              'focus-visible:ring-grape-500 dark:focus-visible:ring-canary-500': darkMode === 'auto',
            },
          ]"
          v-bind="computeTabAttributes(tab)"
          :href="tab.href"
          :color-scheme="OzPlainButtonColorScheme.Bare"
          :size-preset="sizePreset"
          :dark-mode="darkMode"
          :text="tab.iconName ? undefined : tab.text"
          :title="tab.text"
          :disabled="tab.disabled"
          :data-testid="`${tab.text}Button`"
          @click="emit('activate-tab', $event, { key: tab.key })"
        >
          <OzIcon
            v-if="tab.iconName"
            :name="currentTabId === tab.key && tab.activeIconName ? tab.activeIconName : tab.iconName"
            :size="tab.iconSize"
          />
        </OzPlainButton>
      </li>
    </ul>
    <div
      ref="indicator"
      :class="[
        // Position
        'absolute',
        'bottom-0',
        // Shape
        'rounded',
        // Size
        sizePreset === OzTabsSizePreset.Small && 'h-[1.5px]',
        sizePreset === OzTabsSizePreset.ExtraSmall && 'h-0.25',
        // Appearance
        {
          'bg-grape-500': darkMode === false,
          'bg-canary-500': darkMode === true,
          'bg-grape-500 dark:bg-canary-500': darkMode === 'auto',
        },
        // Animation
        'transition-all',
        'motion-reduce:transition-none',
      ]"
    />
  </component>
</template>
